// Content for the per-job pages under the electrician, handyman and plumber hubs,
// rendered by components/index/service-category/handyman/HandymanJobPage.js.
//
// Rules for this file:
//  - No prices in the copy. `{from}` is replaced at render time with the lowest
//    live price among `priceItemNames`, so the text cannot drift from the API.
//  - `priceItemNames` must match `itemName` in GetHandymanPriceList exactly.
//    A name that no longer exists is logged at build time by findItems().
//  - Credentials: say "licensed electrician" and nothing stronger. Hours:
//    technicians attend 8am-10pm daily; requests are accepted any time. Do not
//    write "24 hour" or "24/7".
//  - Each page's FAQs are unique to that URL (see the note in utils/faq.js).

export const ELECTRICIAN_HUB = '/sg/electrician-services-singapore'
export const HANDYMAN_HUB = '/sg/handyman-singapore-services'
export const PLUMBER_HUB = '/sg/plumber-singapore-services'
export const ELECTRICIAN_PRICE_PAGE = `${ELECTRICIAN_HUB}/electrician-price-singapore`
export const HANDYMAN_PRICE_PAGE = `${HANDYMAN_HUB}/handyman-price-list`

// Per-hub settings shared by the job page template and HandymanNavBar.
// Plumbing has no price page of its own; its prices are the Plumbing
// section of the handyman price list.
export const HUB_META = {
    [ELECTRICIAN_HUB]: {
        hubLabel: 'Electrician Services Singapore',
        serviceLabel: 'electrician',
        handymanType: 'Electrician',
        pricePage: ELECTRICIAN_PRICE_PAGE,
        priceLabel: 'Electrician Price List',
        allowedServiceTypes: ['Electrical'],
        allowedCategories: ['Electrical'],
        whatsAppText: "Hi, I'd like to book an electrician.",
    },
    [HANDYMAN_HUB]: {
        hubLabel: 'Handyman Services Singapore',
        serviceLabel: 'handyman',
        handymanType: 'Handyman',
        pricePage: HANDYMAN_PRICE_PAGE,
        priceLabel: 'Handyman Price List',
        whatsAppText: "Hi, I'd like to book a handyman.",
    },
    [PLUMBER_HUB]: {
        hubLabel: 'Plumber Services Singapore',
        serviceLabel: 'plumber',
        handymanType: 'Plumber',
        pricePage: `${HANDYMAN_PRICE_PAGE}#prices-plumbing`,
        priceLabel: 'Plumbing Prices',
        allowedServiceTypes: ['Bathroom', 'Kitchen'],
        // Old names kept until the new price list reaches production.
        allowedCategories: ['Plumbing', 'Kitchen', 'Bathroom'],
        whatsAppText: "Hi, I'd like to book a plumber.",
    },
}

const ELECTRICIAN_IMAGE = 'handyman/electrician-installing-lights.jpg'

export const handymanJobPages = {
    'power-trip-repair': {
        hubPath: ELECTRICIAN_HUB,
        technician: 'electrician',
        // Booking widget tab opened by default.
        bookingTab: 'Electrical',
        breadcrumbTitle: 'Power Trip Repair',
        title: 'Power Trip Repair Singapore | From {from} | StringsSG',
        metaDescription: 'Power keeps tripping? A licensed electrician traces the faulty circuit and fixes it at a fixed price from {from}. Same-day slots, 8am to 10pm daily.',
        h1: 'Power Trip Repair in Singapore',
        heroImage: ELECTRICIAN_IMAGE,
        heroAlt: 'Electrician checking a tripped circuit in a Singapore home',
        serviceType: 'Electrical Fault Repair',
        priceItemNames: [
            'Replace power socket or switch',
            'Replace circuit breaker',
            'Troubleshoot and repair faulty wiring',
        ],
        intro: [
            'A power trip is your distribution board doing its job: it cuts the supply when it detects an overload, a short circuit or current leaking to earth. If it happens once, it may be a one-off. If it keeps happening, something on that circuit is faulty and needs a licensed electrician.',
            'Our electricians isolate each circuit at the distribution board to find the faulty one before replacing anything, so you only pay for the part that is actually broken. Repairs start from {from} at a fixed price you see before you book.',
        ],
        whatsIncluded: [
            'Circuit-by-circuit isolation at the distribution board to locate the fault',
            'Checking sockets, switches and appliances on the affected circuit for water ingress or burnt terminals',
            'Replacing the faulty socket, switch or circuit breaker, or repairing damaged wiring',
            'Restoring power and re-testing the circuit before the electrician leaves',
        ],
        signsHeading: 'Common causes of power trips',
        signs: [
            'A faulty appliance, often a water heater, kettle, washing machine or fridge',
            'Water getting into a socket or switch in the kitchen, bathroom or on the balcony',
            'Too many high-draw appliances on one circuit',
            'Loose or overheating wiring behind a socket, usually with a burning smell',
            'An ageing circuit breaker or residual current device that trips too easily',
        ],
        priceFactors: [
            'What the fault turns out to be: a socket replacement costs less than a wiring repair',
            'How many circuits or points are affected',
            'Whether a part such as a circuit breaker needs replacing',
        ],
        faqs: [
            {
                question: 'Why does my power trip when it rains?',
                answer: 'Rain-related tripping usually means moisture is reaching an outdoor or balcony socket, a light fitting, or a cable with damaged insulation. The residual current device detects current leaking to earth and cuts the supply. An electrician can find the affected point and replace or seal it.',
            },
            {
                question: 'Can I reset the breaker myself?',
                answer: 'You can try once. Switch off or unplug the appliances on that circuit, then switch the breaker back on. If it trips again straight away, or trips each time you plug in a particular appliance, stop resetting it and book an electrician, because repeated resetting can hide a real fault.',
            },
            {
                question: 'How much does power trip repair cost in Singapore?',
                answer: 'With StringsSG, power trip repairs are priced per job and start from {from}. The final price depends on what the electrician finds: replacing a socket or switch, replacing a circuit breaker, or repairing wiring. The price is shown before you confirm.',
            },
            {
                question: 'Can you come today if my power keeps tripping?',
                answer: 'We accept same-day requests and technicians attend between 8am and 10pm daily, subject to availability in your area. Mark the booking as urgent or message us on WhatsApp. Requests sent overnight are picked up first thing the next morning.',
            },
        ],
        relatedSlugs: ['db-box-replacement', 'water-heater-installation', 'ceiling-fan-installation'],
    },

    'db-box-replacement': {
        hubPath: ELECTRICIAN_HUB,
        technician: 'electrician',
        // Booking widget tab opened by default.
        bookingTab: 'Electrical',
        breadcrumbTitle: 'DB Box Replacement',
        title: 'DB Box Replacement Singapore | From {from} | StringsSG',
        metaDescription: 'Replace or upgrade your distribution board (DB box) or circuit breaker with a licensed electrician. Fixed price from {from}, HDB and condo.',
        h1: 'DB Box Replacement & Circuit Breaker Upgrade',
        heroImage: ELECTRICIAN_IMAGE,
        heroAlt: 'Licensed electrician working on a distribution board in Singapore',
        serviceType: 'Distribution Board Installation',
        priceItemNames: [
            'Replace circuit breaker',
            'Install electrical distribution board (DB box)',
        ],
        intro: [
            'The distribution board (DB box) splits your home\'s supply into separate circuits and protects each one with a circuit breaker. Older boards in resale HDB flats and condos often lack residual current protection or spare ways for new circuits, and a single worn breaker can cause frequent trips.',
            'StringsSG electricians replace individual circuit breakers or install a complete new distribution board. Prices start from {from}, fixed and shown before you book.',
        ],
        whatsIncluded: [
            'Inspection of the existing board, breakers and incoming wiring',
            'Replacing a faulty circuit breaker, or installing a new distribution board',
            'Power-on testing of every circuit before handover',
        ],
        signsHeading: 'Signs your DB box needs attention',
        signs: [
            'The same breaker trips repeatedly with nothing obviously wrong',
            'A breaker feels warm, buzzes or shows scorch marks',
            'There are no spare ways for a new aircon, water heater or oven circuit',
            'The board is original to an older resale flat and has no residual current device',
        ],
        priceFactors: [
            'Replacing one breaker versus installing a complete new board',
            'The number of circuits to be moved to the new board',
            'Access to the board, for example inside a cabinet or high on a wall',
        ],
        faqs: [
            {
                question: 'How much does a new DB box cost in Singapore?',
                answer: 'StringsSG lists distribution board installation and circuit breaker replacement at fixed prices, starting from {from} for a single breaker. See the price table on this page for the full-board price. You see the exact amount before confirming.',
            },
            {
                question: 'Do I need to replace the whole DB box or just a breaker?',
                answer: 'If one breaker is faulty and the rest of the board is in good condition, replacing that breaker is usually enough. A full replacement is worth considering when the board is old, has no residual current protection, or has no spare ways for new circuits.',
            },
            {
                question: 'Will my power be off during the replacement?',
                answer: 'Yes, the supply to the board has to be switched off while the electrician works on it, so plan for a period without power. Breaker replacements are much shorter than full board installations.',
            },
            {
                question: 'Do you replace DB boxes in HDB flats and condos?',
                answer: 'Yes. Our electricians work in HDB flats, BTO and resale units, condominiums and landed homes across Singapore.',
            },
        ],
        relatedSlugs: ['power-trip-repair', 'water-heater-installation', 'ceiling-fan-installation'],
    },

    'ceiling-fan-installation': {
        hubPath: ELECTRICIAN_HUB,
        technician: 'electrician',
        // Booking widget tab opened by default.
        bookingTab: 'Electrical',
        breadcrumbTitle: 'Ceiling Fan Installation',
        title: 'Ceiling Fan Installation Singapore | From {from} | StringsSG',
        metaDescription: 'Ceiling fan and wall fan installation by licensed electricians in Singapore. Fixed price from {from} per fan, hook and wiring checked. Same-day slots.',
        h1: 'Ceiling Fan Installation in Singapore',
        heroImage: ELECTRICIAN_IMAGE,
        heroAlt: 'Electrician installing a ceiling fan in a Singapore home',
        serviceType: 'Ceiling Fan Installation',
        priceItemNames: [
            'Install a wall fan',
            'Install ceiling fan',
        ],
        intro: [
            'A ceiling fan is one of the cheapest ways to keep a Singapore home cool, but it hangs from the ceiling and runs off your lighting circuit, so it needs to be mounted and wired properly. Our electricians install new fans, replace old ones and fit wall fans in bedrooms, living rooms and balconies.',
            'Installation starts from {from} per fan at a fixed price. Before mounting, the electrician checks the ceiling hook and the wiring so the fan is secure and the circuit is not overloaded.',
        ],
        whatsIncluded: [
            'Checking the ceiling hook or mounting point can take the fan\'s weight',
            'Assembling and mounting the fan, including the blades and canopy',
            'Connecting the fan to the existing point and fan regulator or remote receiver',
            'Testing every speed setting and the light kit, if the fan has one',
        ],
        signsHeading: 'When to call an electrician for a fan',
        signs: [
            'Replacing an old fan that wobbles, hums or runs slowly',
            'Installing a fan where there is an existing fan point',
            'Swapping a light fitting for a fan with a light kit',
            'Fitting a wall fan in a kitchen, balcony or small room',
        ],
        priceFactors: [
            'Ceiling fan versus wall fan',
            'Number of fans installed in the same visit',
            'Whether a new fan point needs to be added where none exists',
        ],
        faqs: [
            {
                question: 'How much does ceiling fan installation cost in Singapore?',
                answer: 'StringsSG installs fans at a fixed price per fan, starting from {from}. Wall fans and ceiling fans are listed separately in the price table on this page, and you see the total before you confirm.',
            },
            {
                question: 'Do I need to buy the fan myself?',
                answer: 'Yes. The price covers installation. Buy the fan you want and have it at home on the day, and the electrician will assemble and install it.',
            },
            {
                question: 'Can you remove my old ceiling fan?',
                answer: 'Yes. When you replace a fan, the electrician takes down the old one before installing the new one. Let us know when booking so the right time is allowed.',
            },
            {
                question: 'Can a ceiling fan be installed in an HDB flat without a fan point?',
                answer: 'A fan needs a suitable mounting point and a power supply. If there is no existing fan or light point where you want the fan, mention it when booking so the electrician can advise on adding one.',
            },
        ],
        relatedSlugs: ['power-trip-repair', 'water-heater-installation', 'db-box-replacement'],
    },

    'water-heater-installation': {
        hubPath: ELECTRICIAN_HUB,
        technician: 'electrician',
        // Booking widget tab opened by default.
        bookingTab: 'Electrical',
        breadcrumbTitle: 'Water Heater Installation',
        title: 'Water Heater Installation & Repair Singapore | From {from}',
        metaDescription: 'Instant and storage water heater installation, replacement and repair in Singapore, plus 20A heater points. Fixed prices from {from}. Same-day slots.',
        h1: 'Water Heater Installation & Repair in Singapore',
        heroImage: 'handyman/handyman-plumber-fixing-kitchen-pipes.jpg',
        heroAlt: 'Technician installing a water heater in a Singapore bathroom',
        serviceType: 'Water Heater Installation',
        priceItemNames: [
            'Replace instant water heater',
            'Replace storage water heater',
            'Supply and install 20AMP water heater point (non-conceal)',
        ],
        intro: [
            'No hot water, a heater that trips the power, or a leak at the connection are the most common water heater problems in Singapore homes. We replace both instant heaters and storage heaters, and install the dedicated 20A water heater point that a new heater needs.',
            'Prices start from {from} and are fixed per job, so you know the cost before the technician arrives.',
        ],
        whatsIncluded: [
            'Checking the old heater and the existing water and power points',
            'Removing the old unit and installing the new instant or storage heater',
            'Connecting the water inlet and outlet and checking for leaks',
            'Supplying and installing a 20A water heater point where one is needed',
            'Testing hot water flow and the electrical supply before handover',
        ],
        signsHeading: 'Signs your water heater needs attention',
        signs: [
            'The power trips when the heater is switched on',
            'Water is lukewarm or takes much longer to heat up',
            'Water is leaking from the tank, pipes or connections',
            'The heater makes knocking or hissing sounds, or the water is discoloured',
        ],
        priceFactors: [
            'Instant heater versus storage heater: storage units are larger and take longer to install',
            'Repair versus full replacement',
            'Whether a new 20A heater point is needed',
        ],
        faqs: [
            {
                question: 'How much does water heater installation cost in Singapore?',
                answer: 'StringsSG water heater jobs start from {from}. Replacing an instant heater costs less than a storage heater, and adding a 20A heater point is listed separately. Every price is in the table on this page and is fixed before you book.',
            },
            {
                question: 'Why does my water heater trip the power?',
                answer: 'A heater that trips the breaker usually has a failed heating element, water inside the electrical housing, or a faulty switch or point. Stop using it and book a repair, because the fault is on a circuit that also involves water.',
            },
            {
                question: 'Should I choose an instant or storage water heater?',
                answer: 'Instant heaters are compact and heat water as it flows, which suits most HDB bathrooms with one shower. Storage heaters hold a tank of hot water, which suits households that need hot water at several taps or run a rain shower.',
            },
            {
                question: 'Do I need a new power point for a new water heater?',
                answer: 'A water heater should run on its own correctly rated point. If your bathroom does not have one, or you are changing from instant to storage, add a 20A water heater point to the booking.',
            },
        ],
        relatedSlugs: ['power-trip-repair', 'db-box-replacement', 'ceiling-fan-installation'],
    },

    'tv-mounting': {
        hubPath: HANDYMAN_HUB,
        technician: 'handyman',
        // Booking widget tab opened by default.
        bookingTab: 'Electrical',
        breadcrumbTitle: 'TV Wall Mounting',
        title: 'TV Wall Mounting Singapore | Bracket Install from {from}',
        metaDescription: 'TV bracket installation and wall mounting in Singapore, plus mirrors, shelves and artwork. Fixed prices from {from}, HDB and condo, same-day slots.',
        h1: 'TV Wall Mounting & Bracket Installation',
        heroImage: 'handyman/handyman-install-tv.jpg',
        heroAlt: 'Handyman mounting a TV on a wall bracket in a Singapore living room',
        serviceType: 'TV Wall Mounting',
        priceItemNames: [
            'Hang Pictures / Artwork',
            'Hang Mirror',
            'Wall Mounted Shelves',
            'Install TV bracket for wall mount',
        ],
        intro: [
            'Wall mounting frees up floor space and puts the screen at the right viewing height, but a TV is heavy and HDB and condo walls vary, so the bracket needs the right fixings in the right place. Our technicians mount TV brackets and hang mirrors, shelves and artwork across Singapore.',
            'Prices start from {from} at a fixed rate, shown before you book.',
        ],
        whatsIncluded: [
            'Agreeing the height and position with you before drilling',
            'Drilling and fixing the bracket with fixings suited to the wall',
            'Mounting the TV and checking it is level and secure',
            'Hanging mirrors, artwork and wall shelves in the same visit, if booked',
        ],
        signsHeading: 'Popular wall mounting jobs',
        signs: [
            'Mounting a new TV in the living room or bedroom',
            'Moving a TV bracket to a new wall after renovation',
            'Hanging a full-length or bathroom mirror',
            'Putting up floating shelves, frames and artwork',
        ],
        priceFactors: [
            'The item being mounted: a TV bracket takes longer than a picture',
            'The number of items mounted in the same visit',
            'Wall type and whether cables need to be tidied',
        ],
        faqs: [
            {
                question: 'How much does TV wall mounting cost in Singapore?',
                answer: 'StringsSG wall mounting starts from {from}. TV bracket installation, mirrors, shelves and artwork each have their own fixed price in the table on this page, and you see the total before confirming.',
            },
            {
                question: 'Do I need to provide the TV bracket?',
                answer: 'Yes. The price covers installation, so have a bracket that matches your TV\'s size and weight ready on the day. Most TVs come with the mounting screws, and the bracket box lists the TV sizes it supports.',
            },
            {
                question: 'Can you mount a TV on an HDB wall?',
                answer: 'Yes. Technicians check the wall before drilling and use fixings suited to it. Tell us the wall type and TV size when booking so the right tools are brought.',
            },
            {
                question: 'Can you hang mirrors and shelves in the same visit?',
                answer: 'Yes. Add each item to the same booking and the technician will do them together, which saves you arranging a second visit.',
            },
        ],
        relatedSlugs: ['curtain-rail-installation', 'furniture-assembly', 'door-lock-replacement'],
    },

    'curtain-rail-installation': {
        hubPath: HANDYMAN_HUB,
        technician: 'handyman',
        // Booking widget tab opened by default.
        bookingTab: 'Door/Cabinet',
        breadcrumbTitle: 'Curtain Rail Installation',
        title: 'Curtain Rail & Blinds Installation Singapore | From {from}',
        metaDescription: 'Curtain rod, curtain track and blinds installation in Singapore by StringsSG handymen. Fixed price from {from}, HDB, BTO and condo. Book online.',
        h1: 'Curtain Rail & Blinds Installation in Singapore',
        heroImage: 'handyman/handyman-singapore.jpg',
        heroAlt: 'Handyman installing a curtain rail in a Singapore flat',
        serviceType: 'Curtain Rail Installation',
        priceItemNames: [
            'Installing Curtain Rods',
            'Installing Curtain Blinds/Track',
        ],
        intro: [
            'Curtain rods, tracks and blinds are usually the first things new BTO and resale owners need fitted, and they have to be level, aligned with the window and fixed firmly enough to take daily pulling. Our handymen install curtain rods, curtain tracks and blinds across Singapore.',
            'Prices start from {from} at a fixed rate, shown before you book.',
        ],
        whatsIncluded: [
            'Measuring and marking the position so the rail is level and centred',
            'Drilling and fixing brackets into the wall or ceiling',
            'Mounting the rod, track or blinds and checking they run smoothly',
        ],
        signsHeading: 'Common curtain and blinds jobs',
        signs: [
            'Fitting curtains in a new BTO or resale flat',
            'Replacing a sagging or broken curtain rod',
            'Switching from a rod to a ceiling-mounted track',
            'Installing roller or venetian blinds',
        ],
        priceFactors: [
            'Rod versus track or blinds',
            'The number of windows done in the same visit',
            'Ceiling or wall mounting and the wall type',
        ],
        faqs: [
            {
                question: 'How much does curtain rail installation cost in Singapore?',
                answer: 'StringsSG curtain rod and curtain track or blinds installation start from {from} each, at fixed prices listed on this page. You see the total before you confirm.',
            },
            {
                question: 'Do you supply the curtain rail or blinds?',
                answer: 'The listed price is for installation. Buy the rod, track or blinds you want and have them ready on the day.',
            },
            {
                question: 'Can you install curtain tracks on the ceiling?',
                answer: 'Yes. Ceiling-mounted tracks are common in BTO flats and give a floor-to-ceiling look. Mention it when booking so the technician brings the right fixings.',
            },
            {
                question: 'Can you install curtains for a whole BTO flat in one visit?',
                answer: 'Yes. Add every window to the same booking and the technician will do them together.',
            },
        ],
        relatedSlugs: ['tv-mounting', 'furniture-assembly', 'door-lock-replacement'],
    },

    'furniture-assembly': {
        hubPath: HANDYMAN_HUB,
        technician: 'handyman',
        // Booking widget tab opened by default.
        bookingTab: 'Door/Cabinet',
        breadcrumbTitle: 'Furniture Assembly',
        title: 'Furniture Assembly Singapore | From {from} | StringsSG',
        metaDescription: 'Bed frame assembly, wall shelves and cabinet hinge repair in Singapore. Fixed prices from {from}, HDB, BTO and condo. Book a StringsSG handyman online.',
        h1: 'Furniture Assembly & Repair in Singapore',
        heroImage: 'handyman/handyman-assembles-bed.jfif',
        heroAlt: 'Handyman assembling a bed frame in a Singapore bedroom',
        serviceType: 'Furniture Assembly',
        priceItemNames: [
            'Cabinet/cupboard hinge replacement',
            'Wall Mounted Shelves',
            'Fix/install bedframe',
            'Supply & replace cupboard hydraulic hinges (1 pair)',
        ],
        intro: [
            'Flat-pack furniture saves money but takes hours to build, and a wobbly bed frame or a sagging cabinet door is easy to fix with the right tools. Our handymen assemble and fix bed frames, put up wall shelves and replace cabinet and cupboard hinges.',
            'Prices start from {from} at a fixed rate. For other furniture, such as wardrobes or desks, message us on WhatsApp with the item and we will advise before you book.',
        ],
        whatsIncluded: [
            'Assembling or re-tightening bed frames',
            'Replacing cabinet and cupboard hinges, including soft-close hydraulic hinges',
            'Installing wall-mounted shelves',
            'Checking that doors close properly and frames are stable before handover',
        ],
        signsHeading: 'Common furniture jobs',
        signs: [
            'A new bed frame that needs assembling',
            'A bed frame that creaks or wobbles',
            'Cabinet doors that sag, will not close or have a broken hinge',
            'Adding storage with wall-mounted shelves',
        ],
        priceFactors: [
            'The type of furniture or fitting',
            'Number of hinges or items in the same visit',
            'Whether replacement parts are needed',
        ],
        faqs: [
            {
                question: 'How much does furniture assembly cost in Singapore?',
                answer: 'StringsSG furniture jobs start from {from}. Bed frame assembly, wall shelves and hinge replacement each have a fixed price in the table on this page, and you see the total before confirming.',
            },
            {
                question: 'Can you assemble IKEA furniture?',
                answer: 'Bed frames and wall shelves are listed at fixed prices. For wardrobes, desks and other flat-pack items, message us on WhatsApp with the item name so we can advise before you book.',
            },
            {
                question: 'Can you fix a cabinet door that will not close?',
                answer: 'Yes. A sagging or misaligned cabinet door is usually a worn or loose hinge. The technician can adjust it or replace it, including soft-close hydraulic hinges.',
            },
            {
                question: 'Do you bring the replacement hinges?',
                answer: 'The technician can supply parts at additional cost, or use parts you have already bought. The cost is agreed before the work starts.',
            },
        ],
        relatedSlugs: ['tv-mounting', 'curtain-rail-installation', 'door-lock-replacement'],
    },

    'door-lock-replacement': {
        hubPath: HANDYMAN_HUB,
        technician: 'handyman',
        // Booking widget tab opened by default.
        bookingTab: 'Door/Cabinet',
        breadcrumbTitle: 'Door Lock Replacement',
        title: 'Door Lock Replacement Singapore | From {from} | StringsSG',
        metaDescription: 'Door and gate lock replacement, door handle and window lock repair in Singapore. Fixed prices from {from}, HDB and condo. Book a handyman online.',
        h1: 'Door Lock & Handle Replacement in Singapore',
        heroImage: 'handyman/chinese-handyman-with-hammer.jpg',
        heroAlt: 'Handyman replacing a door lock in a Singapore home',
        serviceType: 'Door Lock Replacement',
        priceItemNames: [
            'Door handle/knob replacement',
            'Fix window lock/hinge',
            'Door/Gate lock replacement',
        ],
        intro: [
            'A stiff lock, a loose door handle or a window that will not latch is a security problem as well as an annoyance. Our handymen replace door and gate locks, door handles and knobs, and fix window locks and hinges in HDB flats, condos and landed homes.',
            'Prices start from {from} at a fixed rate, shown before you book.',
        ],
        whatsIncluded: [
            'Removing the old lock, handle or knob',
            'Fitting the new lock set and aligning it with the strike plate',
            'Fixing or replacing window locks and hinges',
            'Testing that the door or window locks and opens smoothly',
        ],
        signsHeading: 'When to replace a lock or handle',
        signs: [
            'The key is stiff, sticks or only turns with force',
            'The handle is loose, drooping or no longer springs back',
            'You have moved into a new home and want the locks changed',
            'A window will not latch or the hinge is broken',
        ],
        priceFactors: [
            'Door lock versus handle or window lock',
            'Main door, bedroom door or gate',
            'The number of locks or handles in the same visit',
        ],
        faqs: [
            {
                question: 'How much does it cost to change a door lock in Singapore?',
                answer: 'StringsSG lock and handle jobs start from {from}. Door and gate lock replacement, handle replacement and window lock repair each have a fixed price in the table on this page.',
            },
            {
                question: 'Should I change the locks after moving into a resale flat?',
                answer: 'It is a sensible step, because you cannot know how many keys the previous owners handed out. Replacing the main door and gate locks gives you the only set of keys.',
            },
            {
                question: 'Do I need to buy the new lock?',
                answer: 'You can buy the lock set yourself, or the technician can supply one at additional cost. The cost is agreed before the work starts.',
            },
            {
                question: 'Can you fix a window that will not lock?',
                answer: 'Yes. Window lock and hinge repair is listed at a fixed price. The technician will adjust or replace the lock or hinge so the window closes securely.',
            },
        ],
        relatedSlugs: ['tv-mounting', 'curtain-rail-installation', 'furniture-assembly'],
    },

    // Plumbing problem pages. These target high-volume problem searches
    // ("clogged toilet bowl", "water leak from faucet"), so they add a `diy`
    // block: what the visitor can safely try first, and when to stop and book.
    // They are also listed on /sg/blogs/repair-renovation via utils/sg-blogs-data.js.

    'leaking-faucet-sink-pipe-repair': {
        hubPath: PLUMBER_HUB,
        // Heading above the booking widget (defaults to 'Book <breadcrumbTitle> Online').
        bookHeading: 'Book a Plumber to Fix the Leak',
        technician: 'plumber',
        // Booking widget tab opened by default.
        bookingTab: 'Kitchen',
        breadcrumbTitle: 'Faucet, Sink & Pipe Leak Repair',
        title: 'Faucet, Sink & Pipe Leak Repair Singapore | From {from}',
        metaDescription: 'Water leaking from a faucet, sink or pipe? Find the cause, what to do right now, and book a plumber at a fixed price from {from}. Same-day slots.',
        h1: 'Water Leak From a Faucet, Sink or Pipe: Causes and Repair',
        heroImage: 'handyman/plumber-fixing-kitchen-sink-tap.jpg',
        heroAlt: 'Plumber repairing a leaking kitchen faucet and sink pipe in Singapore',
        serviceType: 'Water Leak Repair',
        priceItemNames: [
            'Fix water leak from faucet, sink or pipes',
            'Replace kitchen faucet',
            'Supply & replace sink/basin/cistern flexible hose',
            'Repair leak at exposed copper/uPVC pipe (incl. material)',
        ],
        intro: [
            'A dripping faucet, a puddle under the sink or a damp patch around a pipe joint usually has a simple cause: a worn part inside the faucet, a loose or corroded flexible hose, or a failed seal at a joint. Left alone, even a slow drip wastes water, stains basins and can rot the cabinet under the sink.',
            'This guide explains how to find where the water is coming from and what you can safely do straight away. When it needs a plumber, StringsSG repairs faucet, sink and exposed pipe leaks from {from} at a fixed price you see before you book.',
        ],
        diy: {
            heading: 'What to Do Right Now',
            steps: [
                'Turn off the water. Use the small isolation valve on the pipe under the sink or basin. If there is none, or the leak is on a pipe, close the main water valve, usually next to your water meter.',
                'Protect the area. Dry the floor and cabinet, and put a bowl or towel under the drip so you can see how fast it leaks.',
                'Find the source. Dry everything with a cloth, turn the water back on briefly and watch: the spout (worn cartridge or washer), the base of the faucet, the flexible hoses, the trap under the sink or a pipe joint.',
                'Check the easy fixes. A flexible hose connection that has worked loose can be tightened gently by hand. Do not over-tighten, as that cracks fittings.',
                'Take a photo of the leak and the parts around it. It helps the plumber bring the right parts.',
            ],
            stopIf: [
                'Water is coming from inside a wall, from the floor tiles or from the ceiling',
                'A pipe has burst or is spraying',
                'Water is near a power socket, switch or appliance',
                'The leak continues with the valve closed',
            ],
        },
        whatsIncluded: [
            'Tracing the leak to the faucet, hose, trap or pipe joint',
            'Repairing the leak, or replacing the faucet or flexible hose where that is the fix',
            'Repairing leaks on exposed copper or uPVC pipes',
            'Running the water and checking every joint is dry before handover',
        ],
        signsHeading: 'Common causes of faucet, sink and pipe leaks',
        signs: [
            'A worn cartridge or washer inside the faucet, which causes a steady drip from the spout',
            'A loose, kinked or corroded flexible hose under the sink or basin',
            'A failed seal where the sink meets the waste pipe or bottle trap',
            'A cracked or corroded joint on an exposed copper or uPVC pipe',
            'High water pressure wearing out seals faster than usual',
        ],
        priceFactors: [
            'Whether the faucet can be repaired or needs replacing',
            'Faucet and hose leaks versus a leak on the pipe itself',
            'Exposed pipes are quicker to reach than pipes concealed behind tiles',
        ],
        faqs: [
            {
                question: 'How much does it cost to fix a leaking faucet in Singapore?',
                answer: 'StringsSG fixes water leaks from faucets, sinks and pipes from {from} at a fixed price. Replacing the faucet, a flexible hose or repairing an exposed pipe each have their own price in the table on this page, and you see the total before you confirm.',
            },
            {
                question: 'Why is my kitchen sink leaking underneath?',
                answer: 'Leaks under a sink usually come from a loose or worn flexible hose, the seal where the sink joins the waste pipe, or the bottle trap. Dry everything, run the tap and watch each joint to see which one drips first.',
            },
            {
                question: 'Is a dripping tap worth fixing?',
                answer: 'Yes. A constant drip adds up to a lot of wasted water over a month, shows up on your water bill, and the worn part usually gets worse over time. It is normally a quick repair.',
            },
            {
                question: 'Water is leaking from my ceiling. Who do I call?',
                answer: 'A ceiling leak often comes from the unit above rather than your own pipes. In an HDB flat, contact your HDB Branch to investigate inter-floor leakage. If the leak is from your own exposed pipes, a plumber can repair it.',
            },
        ],
        relatedSlugs: ['clogged-kitchen-sink', 'clogged-toilet-bowl', 'clogged-bathroom-drain'],
    },

    'clogged-toilet-bowl': {
        hubPath: PLUMBER_HUB,
        // Heading above the booking widget (defaults to 'Book <breadcrumbTitle> Online').
        bookHeading: 'Book a Plumber to Clear Your Toilet',
        technician: 'plumber',
        // Booking widget tab opened by default.
        bookingTab: 'Bathroom',
        breadcrumbTitle: 'Clogged Toilet Bowl',
        title: 'Clogged Toilet Bowl Singapore | Choke Clearing From {from}',
        metaDescription: 'Toilet bowl clogged or choked? Try these safe fixes first, then book a plumber to clear it at a fixed price from {from}. Same-day slots, 8am to 10pm.',
        h1: 'Clogged Toilet Bowl: How to Unclog It and When to Call a Plumber',
        heroImage: 'handyman/handyman-plumber-fixing-kitchen-pipes.jpg',
        heroAlt: 'Plumber clearing a choked toilet bowl in a Singapore bathroom',
        serviceType: 'Toilet Choke Clearing',
        priceItemNames: [
            'Clear clogged toilet bowl',
            'Replace toilet flush system',
            'Supply & replace toilet cistern siphon',
            'Install toilet bowl set',
        ],
        intro: [
            'A clogged toilet bowl, or toilet choke, is one of the most common plumbing problems in Singapore homes. The water rises instead of draining, drains very slowly, or gurgles when you flush. Most chokes are caused by too much toilet paper or by wet wipes and other items that do not break down.',
            'Many partial blockages can be cleared safely with a plunger. When they cannot, StringsSG plumbers clear clogged toilet bowls from {from} at a fixed price you see before you book.',
        ],
        diy: {
            heading: 'How to Unclog a Toilet Safely',
            steps: [
                'Stop flushing. Each flush adds water and can make the bowl overflow.',
                'If the water is high, wait 10 to 15 minutes for it to drain down a little, and lay old towels around the base.',
                'Use a flange plunger, the kind with a soft extension that fits the toilet outlet. Seal it over the hole and push and pull firmly 10 to 15 times without breaking the seal.',
                'If the water starts to drain, flush once to check. Repeat the plunging if it is still slow.',
                'Avoid chemical drain cleaners in a fully blocked toilet. They can splash back, and they rarely clear a solid blockage.',
            ],
            stopIf: [
                'An object such as a toy, phone or sanitary product has gone down',
                'Water rises from the floor trap or shower drain when you flush',
                'Other drains in the flat are also slow or gurgling',
                'Plunging has not worked after a few attempts',
            ],
        },
        whatsIncluded: [
            'Clearing the blockage in the toilet bowl and its outlet',
            'Checking the flush and water discharge once the bowl drains',
            'Replacing a faulty flush system or siphon, if a weak flush is causing repeat chokes',
            'Installing a new toilet bowl set, if the bowl is cracked or needs replacing',
        ],
        signsHeading: 'Common causes of a clogged toilet',
        signs: [
            'Too much toilet paper flushed at once',
            'Wet wipes, including "flushable" ones, which do not break down like toilet paper',
            'Sanitary products, cotton buds and kitchen roll',
            'Objects dropped in by children',
            'A weak flush that does not clear the bowl, often a worn flush system or siphon',
        ],
        priceFactors: [
            'A simple blockage versus one that needs parts replaced',
            'Whether the flush system or siphon is causing repeat blockages',
            'Replacing the whole toilet bowl set costs more than clearing a choke',
        ],
        faqs: [
            {
                question: 'How much does it cost to clear a clogged toilet in Singapore?',
                answer: 'StringsSG clears clogged toilet bowls from {from} at a fixed price. If a faulty flush system or siphon is causing repeat blockages, those repairs are listed separately in the table on this page. You see the total before you confirm.',
            },
            {
                question: 'Can wet wipes clog a toilet?',
                answer: 'Yes. Wet wipes, including many sold as flushable, do not break down the way toilet paper does, and they collect in the outlet and pipes. Put them in the bin instead.',
            },
            {
                question: 'Why does my toilet keep getting clogged?',
                answer: 'Repeated chokes usually mean a weak flush that does not push waste through, a partial blockage further down the pipe, or regular flushing of wipes. A plumber can check the flush system and siphon as well as clearing the blockage.',
            },
            {
                question: 'Who is responsible for a toilet choke in an HDB flat?',
                answer: 'A blockage in your own toilet bowl and the pipe leading from it is usually the owner\'s to fix. If the blockage is in the common sanitary stack shared by several flats, and neighbours are affected too, contact your Town Council.',
            },
        ],
        relatedSlugs: ['clogged-bathroom-drain', 'clogged-kitchen-sink', 'leaking-faucet-sink-pipe-repair'],
    },

    'clogged-kitchen-sink': {
        hubPath: PLUMBER_HUB,
        // Heading above the booking widget (defaults to 'Book <breadcrumbTitle> Online').
        bookHeading: 'Book a Plumber to Unclog Your Sink',
        technician: 'plumber',
        // Booking widget tab opened by default.
        bookingTab: 'Kitchen',
        breadcrumbTitle: 'Clogged Kitchen Sink',
        title: 'Clogged Kitchen Sink Singapore | Unclog From {from}',
        metaDescription: 'Kitchen sink or pipe clogged? Learn what causes it, how to clear it safely yourself, and book a plumber from {from} at a fixed price. Same-day slots.',
        h1: 'Clogged Kitchen Sink or Pipe: Causes, DIY Fixes and Plumber Prices',
        heroImage: 'handyman/plumber-fixing-kitchen-sink-tap.jpg',
        heroAlt: 'Plumber unclogging a kitchen sink pipe in a Singapore flat',
        serviceType: 'Kitchen Sink Unclogging',
        priceItemNames: [
            'Clear clogged kitchen sink or pipe',
            'Supply & replace bottle trap for kitchen sink',
        ],
        intro: [
            'A kitchen sink that drains slowly, gurgles or fills with water is almost always blocked by grease, food scraps or coffee grounds that have built up in the trap or the pipe below it. It usually starts as a slow drain and gets worse over a few weeks.',
            'You can often clear a partial blockage yourself. When it will not clear, or keeps coming back, StringsSG plumbers unclog kitchen sinks and pipes from {from} at a fixed price you see before you book.',
        ],
        diy: {
            heading: 'How to Unclog a Kitchen Sink Yourself',
            steps: [
                'Scoop out standing water into a bucket so you can work on the drain.',
                'Pour a kettle of hot water, just off the boil, slowly down the drain to soften grease. Avoid pouring fully boiling water into uPVC pipes.',
                'Pour half a cup of baking soda down the drain, then a cup of vinegar. Leave it for 15 minutes, then flush with hot water.',
                'Use a sink plunger. If you have a double sink, block the second drain with a wet cloth so the pressure goes into the blockage.',
                'Clean the trap. Put a bucket under the U-shaped or bottle trap below the sink, unscrew it by hand, empty it and rinse it before refitting.',
            ],
            stopIf: [
                'The sink clears for a few days and then blocks again',
                'Water backs up into the other sink or a floor trap',
                'The trap or pipe joints leak after you refit them',
                'You can smell sewage from the sink',
            ],
        },
        whatsIncluded: [
            'Clearing the blockage in the sink waste, trap and pipe',
            'Flushing the line and checking the sink drains freely',
            'Supplying and replacing the bottle trap if it is cracked, leaking or worn out',
            'Checking the joints under the sink are dry before handover',
        ],
        signsHeading: 'What clogs a kitchen sink',
        signs: [
            'Cooking oil and grease, which set in the pipe as they cool',
            'Rice, noodles and other food scraps',
            'Coffee grounds and tea leaves',
            'Soap scum combining with grease',
            'A dirty or damaged bottle trap',
        ],
        priceFactors: [
            'How far down the pipe the blockage is',
            'Whether the bottle trap needs replacing',
            'Grease build-up that needs a thorough clean rather than a quick clear',
        ],
        faqs: [
            {
                question: 'How much does it cost to unclog a kitchen sink in Singapore?',
                answer: 'StringsSG clears clogged kitchen sinks and pipes from {from} at a fixed price. Replacing the bottle trap under the sink is listed separately in the table on this page, and you see the total before you confirm.',
            },
            {
                question: 'Can I pour cooking oil down the kitchen sink?',
                answer: 'Avoid it. Oil and grease cool and harden inside the pipe, and they trap food scraps. Let oil cool, wipe the pan with kitchen paper and put the paper in the bin.',
            },
            {
                question: 'Does baking soda and vinegar unclog a sink?',
                answer: 'It can help with a slow drain caused by light grease and soap build-up. It will not clear a solid blockage such as a mass of food scraps, which needs a plunger, a trap clean or a plumber.',
            },
            {
                question: 'Why does my kitchen sink smell?',
                answer: 'A smell usually means food and grease are rotting in the trap. Cleaning the trap and flushing with hot water normally fixes it. If the smell is of sewage, the blockage may be further down the pipe.',
            },
        ],
        relatedSlugs: ['leaking-faucet-sink-pipe-repair', 'clogged-bathroom-drain', 'clogged-toilet-bowl'],
    },

    'clogged-bathroom-drain': {
        hubPath: PLUMBER_HUB,
        // Heading above the booking widget (defaults to 'Book <breadcrumbTitle> Online').
        bookHeading: 'Book a Plumber to Clear Your Drain',
        technician: 'plumber',
        // Booking widget tab opened by default.
        bookingTab: 'Bathroom',
        breadcrumbTitle: 'Clogged Bathroom Drain',
        title: 'Clogged Bathroom Drain Singapore | Floor Trap From {from}',
        metaDescription: 'Bathroom floor trap or drainage pipe clogged? See what causes it, how to clear hair and soap build-up, and book a plumber from {from}. Same-day slots.',
        h1: 'Clogged Bathroom Drain or Floor Trap: How to Clear It',
        heroImage: 'handyman/handyman-plumber-fixing-kitchen-pipes.jpg',
        heroAlt: 'Plumber clearing a clogged bathroom floor trap in Singapore',
        serviceType: 'Drain Unclogging',
        priceItemNames: [
            'Clear clogged drainage pipe',
        ],
        intro: [
            'When shower water pools on the bathroom floor or the floor trap drains slowly, the drainage pipe is usually clogged with hair and soap scum. After a renovation, grout and cement debris washed into the floor trap can cause the same problem.',
            'Most bathroom drain clogs sit just below the floor trap cover and can be cleared by hand. For blockages further down, StringsSG plumbers clear clogged drainage pipes from {from} at a fixed price you see before you book.',
        ],
        diy: {
            heading: 'How to Clear a Clogged Floor Trap',
            steps: [
                'Put on rubber gloves and lift off the floor trap cover. Most lift straight up or unscrew.',
                'Pull out hair and soap build-up with your fingers or a plastic drain hook. Most blockages are within reach of the top of the trap.',
                'Pour a kettle of hot water, just off the boil, down the drain to melt soap scum.',
                'For a slow drain, use half a cup of baking soda followed by a cup of vinegar, wait 15 minutes, and flush with hot water.',
                'Fit a hair catcher over the floor trap to stop it happening again.',
            ],
            stopIf: [
                'Water comes up through the floor trap when the toilet is flushed',
                'Several drains in the flat are slow at the same time',
                'The blockage is below the reach of the trap',
                'Water is leaking to the unit below',
            ],
        },
        whatsIncluded: [
            'Clearing the blockage in the floor trap and drainage pipe',
            'Removing hair, soap scum and debris from the trap',
            'Flushing the pipe and checking water drains away freely',
        ],
        signsHeading: 'What clogs a bathroom drain',
        signs: [
            'Hair, the most common cause by far',
            'Soap scum and shampoo residue building up on the pipe walls',
            'Small items such as bottle caps and sachets',
            'Grout, cement or tile debris left after renovation works',
        ],
        priceFactors: [
            'A blockage at the floor trap versus further down the drainage pipe',
            'Debris from renovation, which can take longer to clear than hair',
            'Clearing more than one drain in the same visit',
        ],
        faqs: [
            {
                question: 'How much does it cost to clear a clogged bathroom drain in Singapore?',
                answer: 'StringsSG clears clogged drainage pipes from {from} at a fixed price, shown before you confirm the booking.',
            },
            {
                question: 'Why is water coming up from my bathroom floor trap?',
                answer: 'Water rising from the floor trap means the drain below it is blocked, so water from the shower or basin has nowhere to go. If it rises when you flush the toilet, the blockage is further down the shared pipe and needs a plumber.',
            },
            {
                question: 'How do I stop hair clogging my shower drain?',
                answer: 'Fit a hair catcher over the floor trap, clear it after each wash, and lift the trap cover to clean out build-up every few weeks.',
            },
            {
                question: 'Why does my bathroom drain smell?',
                answer: 'A smelly drain is often a dry floor trap. The trap holds water that blocks sewer smells, and it dries out when the drain is not used. Pour a jug of water in. If the smell stays, hair and soap build-up in the trap is likely the cause.',
            },
        ],
        relatedSlugs: ['clogged-toilet-bowl', 'clogged-kitchen-sink', 'leaking-faucet-sink-pipe-repair'],
    },
}

// Static routes that live inside the WordPress-driven [slug] folder. The
// [slug] getStaticPaths filters these out so a WordPress post with the same
// slug cannot collide with a static page (Next.js fails the build on that).
export const STATIC_HANDYMAN_SLUGS = [
    'handyman-price-list',
    ...Object.keys(handymanJobPages).filter((slug) => handymanJobPages[slug].hubPath === HANDYMAN_HUB),
]

export const jobPagePath = (slug) => `${handymanJobPages[slug].hubPath}/${slug}`

export const jobPagesForHub = (hubPath) =>
    Object.keys(handymanJobPages).filter((slug) => handymanJobPages[slug].hubPath === hubPath)
